import React, { useContext, useState, useEffect, useRef } from "react";
import { ActionType } from "../State/State";
import { AppContext } from "../State/Context";
import { RequestMethod, askAPI } from "../api/api";
import { Player } from "../types";

export const PlayerProfile: React.FC = () => {
  const { state, dispatch } = useContext(AppContext);
  const player = state.player;

  const [newName, setNewName] = useState(player ? player.name : "");
  const [draftHighQuality, setDraftHighQuality] = useState(state.highQuality);
  const [draftInvertMapPan, setDraftInvertMapPan] = useState(state.invertMapPan);
  const [draftHexGridBuffer, setDraftHexGridBuffer] = useState(state.hexGridBuffer);
  const [billingLoading, setBillingLoading] = useState(false);

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (state.isPlayerProfilePopupOpen && player) {
      if (!wasOpenRef.current) {
        setNewName(player.name);
        setDraftHighQuality(state.highQuality);
        setDraftInvertMapPan(state.invertMapPan);
        setDraftHexGridBuffer(state.hexGridBuffer);
      }
      wasOpenRef.current = true;
    } else {
      wasOpenRef.current = false;
    }
  }, [state.isPlayerProfilePopupOpen, player, state.highQuality, state.invertMapPan, state.hexGridBuffer]);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewName(event.target.value);
  };

  const handleSaveSettings = async () => {
    dispatch({ type: ActionType.SET_HIGH_QUALITY, value: draftHighQuality });
    dispatch({ type: ActionType.SET_INVERT_MAP_PAN, value: draftInvertMapPan });
    dispatch({ type: ActionType.SET_HEX_GRID_BUFFER, value: Math.min(2, Math.max(0, draftHexGridBuffer)) });
    const updatedPlayer = await askAPI<Player>(
      "/api/player/edit",
      RequestMethod.POST,
      {
        name: newName.trim(),
      }
    );
    if (updatedPlayer && !("systemMessage" in updatedPlayer)) {
      dispatch({ type: ActionType.UPDATE_PLAYER, player: updatedPlayer });
    }
    dispatch({ type: ActionType.TOGGLE_PLAYER_PROFILE_POPUP });
  };

  const showDeletePopup = async () => {
    dispatch({ type: ActionType.TOGGLE_DELETE_POPUP });
  };

  const openBillingPortal = async () => {
    if (!player?.canManageBilling || billingLoading) return;
    setBillingLoading(true);
    try {
      const res = await askAPI<{ url: string }>(
        "/api/stripe/create-portal-session",
        RequestMethod.POST
      );
      if (res?.url) window.location.href = res.url;
    } finally {
      setBillingLoading(false);
    }
  };

  const btnClass = "button min-w-[160px] h-10 flex items-center justify-center";

  return (
    player && (
      <div className="flex flex-col h-full">
        {player.canManageBilling && (
          <div className="mt-2">
            <div className="font-bold mb-2">Existing billing account</div>
            <div className="text-sm mb-2">Sparks purchases are no longer available.</div>
            <button
              type="button"
              onClick={openBillingPortal}
              disabled={billingLoading}
              className={btnClass}
            >
              {billingLoading ? "…" : "Open billing portal"}
            </button>
          </div>
        )}
        <div className="font-bold mb-2">Map settings</div>
        <div className="flex items-center gap-2 mt-1">
          <input
            type="checkbox"
            id="highQuality"
            checked={draftHighQuality}
            onChange={(e) => setDraftHighQuality(e.target.checked)}
          />
          <label htmlFor="highQuality">High quality tiles</label>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <input
            type="checkbox"
            id="invertMapPan"
            checked={draftInvertMapPan}
            onChange={(e) => setDraftInvertMapPan(e.target.checked)}
          />
          <label htmlFor="invertMapPan">
            Reverse natural map scrolling
          </label>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <label htmlFor="hexGridBuffer">Map grid buffer:</label>
          <select
            id="hexGridBuffer"
            value={draftHexGridBuffer}
            onChange={(e) => setDraftHexGridBuffer(Number(e.target.value))}
            className="border border-black px-2 py-1"
          >
            <option value={0}>none</option>
            <option value={1}>normal</option>
            <option value={2}>extra</option>
          </select>
        </div>
        <div className="flex flex-col items-start space-y-1 mt-4">
          <label htmlFor="newName" className="font-bold">
            Change Name:
          </label>
          <input
            type="text"
            id="newName"
            value={newName}
            onChange={handleNameChange}
            className="border border-black px-2 py-1 focus:outline-none w-full"
          />
        </div>
        <div className="flex justify-between gap-3 mt-6 pt-4 flex-1 items-end">
          <button onClick={showDeletePopup} className={btnClass}>
            Delete My Account
          </button>
          <button onClick={handleSaveSettings} className={btnClass}>
            Save Settings
          </button>
        </div>
      </div>
    )
  );
};
